import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { finalize } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { CashServiceService } from 'src/app/mpesa/services/cash-service.service';
import { TokenStorageService } from 'src/app/core/service/token-storage.service';

interface SetupRowResult {
  rowNumber?: number;
  status?: string;
  message?: string;
  outstationName?: string;
  communityName?: string;
  [key: string]: any;
}

interface SetupSummary {
  templateType?: string;
  processedRows?: number;
  successfulRows?: number;
  skippedRows?: number;
  failedRows?: number;
  rows?: SetupRowResult[];
  [key: string]: any;
}

interface SetupResultSection {
  title: string;
  summary: SetupSummary;
}

@Component({
  selector: 'app-upload-outstation',
  templateUrl: './upload-outstation.component.html',
  styleUrls: ['./upload-outstation.component.sass']
})
export class UploadOutstationComponent implements OnInit {
  @ViewChild('fileInputRef') fileInputRef?: ElementRef<HTMLInputElement>;

  selectedFile: File | null = null;
  uploading = false;
  downloadingTemplate = false;
  uploadMessage: string | null = null;
  resultSections: SetupResultSection[] = [];

  constructor(
    private cashService: CashServiceService,
    private tokenStorage: TokenStorageService,
    private snackBar: MatSnackBar,
    private router: Router
  ) {}

  ngOnInit(): void {}

  openFilePicker(): void {
    this.fileInputRef?.nativeElement.click();
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      this.selectedFile = file;
      this.uploadMessage = null;
      this.resultSections = [];
    }
  }

  downloadTemplate(): void {
    const parishId = this.tokenStorage.getUser()?.parishId;
    if (!parishId) {
      this.showSnackbar('Unable to determine parish. Please log in again.', 'error');
      return;
    }
    this.downloadingTemplate = true;
    this.cashService.downloadBulkSetupTemplate(parishId).pipe(
      finalize(() => { this.downloadingTemplate = false; })
    ).subscribe({
      next: (blob: Blob) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'outstation_community_template.xlsx';
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.setTimeout(() => window.URL.revokeObjectURL(url), 0);
      },
      error: () => {
        this.showSnackbar('Unable to download the template right now.', 'error');
      }
    });
  }

  uploadFile(): void {
    if (!this.selectedFile) {
      this.showSnackbar('Please select a file to upload.', 'warn');
      return;
    }
    const parishId = this.tokenStorage.getUser()?.parishId;
    if (!parishId) {
      this.showSnackbar('Unable to determine parish. Please log in again.', 'error');
      return;
    }

    this.uploading = true;
    this.uploadMessage = null;
    this.resultSections = [];

    this.cashService.uploadBulkSetup(this.selectedFile, parishId).subscribe({
      next: (resp: any) => {
        this.uploading = false;
        this.applyResponse(resp);
        this.showSnackbar(resp?.message || 'Bulk setup processed successfully.', this.resolveToastType(resp));
        this.selectedFile = null;
        this.resetInput();
      },
      error: (error: any) => {
        this.uploading = false;
        const resp = error?.error;
        if (resp) this.applyResponse(resp);
        const body = error?.error;
        const msg = body?.message || (typeof body === 'string' ? body : null) || 'Upload failed. Please try again.';
        this.showSnackbar(msg, 'error');
        this.resetInput();
      }
    });
  }

  goBack(): void {
    this.router.navigate(['/admin/outstation']);
  }

  getRows(summary: SetupSummary | undefined): SetupRowResult[] {
    return Array.isArray(summary?.rows) ? summary.rows : [];
  }

  getProcessedRows(summary: SetupSummary | undefined): number {
    return Number(summary?.processedRows || 0);
  }

  getSuccessfulRows(summary: SetupSummary | undefined): number {
    return Number(summary?.successfulRows || 0);
  }

  getSkippedRows(summary: SetupSummary | undefined): number {
    return Number(summary?.skippedRows || 0);
  }

  getFailedRows(summary: SetupSummary | undefined): number {
    return Number(summary?.failedRows || 0);
  }

  statusClass(status: string | undefined): string {
    switch ((status || '').toUpperCase()) {
      case 'SUCCESS': return 'status-success';
      case 'SKIPPED': return 'status-neutral';
      case 'FAILED':  return 'status-failed';
      default:        return 'status-neutral';
    }
  }

  resolveNameLabel(row: SetupRowResult): string {
    if (row.communityName && row.outstationName) {
      return `${row.communityName} (${row.outstationName})`;
    }
    return row.outstationName || row.communityName || '-';
  }

  trackByRow(index: number, row: SetupRowResult): number | string {
    return row.rowNumber ?? index;
  }

  private applyResponse(response: any): void {
    this.uploadMessage = response?.message || null;
    const entity = response?.entity;
    if (!entity) return;
    this.resultSections = [];
    if (entity.outstationSummary) {
      this.resultSections.push({ title: 'Outstations', summary: entity.outstationSummary });
    }
    if (entity.communitySummary) {
      this.resultSections.push({ title: 'Communities', summary: entity.communitySummary });
    }
  }

  private resolveToastType(response: any): 'success' | 'warn' | 'error' {
    if ((response?.statusCode || 0) >= 400) return 'error';
    if (this.resultSections.some(s => this.getFailedRows(s.summary) > 0)) return 'warn';
    return 'success';
  }

  private showSnackbar(message: string, type: 'success' | 'error' | 'warn'): void {
    this.snackBar.open(message, 'Close', { duration: 4000, panelClass: [type] });
  }

  private resetInput(): void {
    if (this.fileInputRef?.nativeElement) {
      this.fileInputRef.nativeElement.value = '';
    }
  }
}
